import { AddIcon, CopyIcon, CheckCircleIcon, InfoOutlineIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Flex,
  Heading,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Text,
  Textarea,
  Tooltip,
  useClipboard,
  useColorModeValue,
  useDisclosure,
  useToast
} from '@chakra-ui/react';
import AppTableWithPagination from 'components/AppTableWithPagination';
import SidebarWithHeader from 'components/SidebarWithHeader';
import { useFormik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { useCreatePipelineMutation, useGetPipelinesQuery, useGetPixelCodeMutation } from 'store/BmAccounts/bmAccountApi';
import * as Yup from 'yup';

// ---------------------------
// Helpers for Shopify snippet
// ---------------------------
const DEFAULT_HOST = 'https://vdk5xavwyt.ap-northeast-1.awsapprunner.com/';

// Extracts PIXEL from a string that contains: cbq('init', '584457467598467742');
function extractPixelId(pixelCode) {
  if (!pixelCode) return null;
  const re = /cbq\(\s*['"]init['"]\s*,\s*['"](\d{6,})['"]\s*\)/i;
  const m = pixelCode.match(re);
  return m?.[1] || null;
}

// Extracts HOST from: cbq('setHost', 'https://gateway.hustleapp.ai/');
function extractHost(pixelCode) {
  if (!pixelCode) return null;
  const re = /cbq\(\s*['"]setHost['"]\s*,\s*['"]([^'"]+)['"]\s*\)/i;
  const m = pixelCode.match(re);
  if (!m || !m[1]) return null;
  return ensureTrailingSlash(m[1]);
}

// Ensure trailing slash so concatenations like HOST + 'sdk/...'
function ensureTrailingSlash(url) {
  return url.endsWith('/') ? url : url + '/';
}

function buildShopifySnippet({ pixelId, host = DEFAULT_HOST }) {
  if (!pixelId) return '';
  return `const PIXEL = '${pixelId}';
const HOST = '${host}';
!(function (f, b, e, v, n, t, s) {
  if (f.cbq) return; n = f.cbq = function () {
    n.callMethod ?
      n.callMethod.apply(n, arguments) : n.queue.push(arguments);
  };
  if (!f._cbq) f._cbq = n; n.push = n; n.loaded = !0; n.version = '2.0';
  n.queue = []; t = b.createElement(e); t.async = !0; t.src = v;
  s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
})(window, document, 'script', HOST + 'sdk/capig-events.js');

cbq('setHost', HOST);
cbq('init', PIXEL);
cbq('set', 'integrationMethod', 'Shopify');
cbq('loadConfig', PIXEL);

const user_data = {};
function isNullOrEmpty(value) {
  return (value === undefined || value === null ||
    (typeof value === 'object' && Object.keys(value).length === 0) ||
    value === '');
}
function updateUserData(event) {
  if (event.data && event.data.checkout) {
    const checkoutData = event.data.checkout;
    const phone = checkoutData.phone || checkoutData.shippingAddress?.phone || checkoutData.billingAddress?.phone;
    if (!isNullOrEmpty(checkoutData.shippingAddress?.firstName)) { user_data.fn = checkoutData.shippingAddress.firstName; }
    if (!isNullOrEmpty(checkoutData.shippingAddress?.lastName)) { user_data.ln = checkoutData.shippingAddress.lastName; }
    if (!isNullOrEmpty(phone)) { user_data.ph = phone; }
    if (!isNullOrEmpty(checkoutData.shippingAddress?.city)) { user_data.ct = checkoutData.shippingAddress.city; }
    if (!isNullOrEmpty(checkoutData.shippingAddress?.provinceCode)) { user_data.st = checkoutData.shippingAddress.provinceCode; }
    if (!isNullOrEmpty(checkoutData.shippingAddress?.zip)) { user_data.zp = checkoutData.shippingAddress.zip; }
    if (!isNullOrEmpty(checkoutData.shippingAddress?.countryCode)) { user_data.country = checkoutData.shippingAddress.countryCode; }
    if (!isNullOrEmpty(checkoutData.email)) {
      user_data.em = checkoutData.email;
    }
  }
}
function getCartData(event) {
  const contentIds = [];
  let contentType = 'product';
  let numItems = 0;
  for (const item of event.data.checkout.lineItems) {
    const contentId = item.variant?.product?.id || item.variant?.id || item.variant?.sku;
    if (!isNullOrEmpty(contentId)) { contentIds.push(contentId); }
    if (!isNullOrEmpty(item.variant?.product?.id)) {
      contentType = 'product_group';
    }
    const numItem = item.quantity || 1;
    numItems += numItem;
  }
  return {
    content_ids: contentIds,
    content_type: contentType,
    num_items: numItems,
    currency: event.data.checkout.currencyCode || 'USD',
  };
}
function fireCBQEvent(event_name, event_data, event) {
  updateUserData(event);
  const external_id = { external_id: event.clientId };
  const pii_data = { ...user_data, ...external_id };
  if (!isNullOrEmpty(user_data)) cbq('init', PIXEL, pii_data);
  cbq('track', event_name, event_data, { eventID: event.id });
}
analytics.subscribe('page_viewed', event => {
  fireCBQEvent('PageView', {}, event);
});
analytics.subscribe('product_viewed', async event => {
  const content_data = {
    content_ids: [event.data.productVariant.product.id],
    content_type: event.data.productVariant.product.id ? 'product_group' : 'product',
    content_name: event.data.productVariant.product.title,
    content_category: event.data.productVariant.product.type || '',
    currency: event.data.productVariant.price.currencyCode || 'USD',
    value: event.data.productVariant.price.amount,
  };
  fireCBQEvent('ViewContent', content_data, event);
});
analytics.subscribe('search_submitted', async event => {
  fireCBQEvent('Search', { search_string: event.data.searchResult.query }, event);
});
analytics.subscribe('product_added_to_cart', async event => {
  const cart_line = {
    content_ids: [event.data.cartLine?.merchandise?.product?.id],
    content_name: event.data.cartLine?.merchandise?.product?.title,
    currency: event.data.cartLine?.merchandise?.price?.currencyCode || 'USD',
    value: event.data.cartLine?.merchandise?.price?.amount,
    num_items: event.data.cartLine?.quantity || 1,
    content_category: event.data.cartLine?.merchandise?.product?.type || '',
    content_type: event.data.cartLine?.merchandise?.product?.id ? 'product_group' : 'product',
  };
  fireCBQEvent('AddToCart', cart_line, event);
});
analytics.subscribe('checkout_started', async event => {
  const cart_details = {
    ...getCartData(event),
    value: event.data.checkout.subtotalPrice.amount,
  };
  fireCBQEvent('InitiateCheckout', cart_details, event);
});
analytics.subscribe('checkout_completed', async event => {
  const cart_details = {
    ...getCartData(event),
    value: event.data.checkout.totalPrice.amount,
  };
  fireCBQEvent('Purchase', cart_details, event);
});
analytics.subscribe('payment_info_submitted', async event => {
  const event_data = {
    currency: event.data.checkout.currencyCode || 'USD',
    value: event.data.checkout.totalPrice.amount,
  };
  fireCBQEvent('AddPaymentInfo', event_data, event);
});`;
}

function SignalGateway() {
  const textColor = useColorModeValue("brand.900", "#fff");
  const inputBackground = useColorModeValue('gray.100', 'gray.800');
  const textColorSub = useColorModeValue("brand.300", "brand.700");

  const toast = useToast();
  const {
    isOpen: isPixelAddModalOpen,
    onOpen: onPixelAddModalOpen,
    onClose: onPixelAddModalClose,
  } = useDisclosure();

  const {
    isOpen: isGetCodeModalOpen,
    onOpen: onGetCodeModalOpen,
    onClose: onGetCodeModalClose,
  } = useDisclosure();

  const [searchValue, setsearchValue] = useState('');
  const [pixelCode, setPixelCode] = useState("");
  const [sourceName, setSourceName] = useState("");
  const [integrationType, setIntegrationType] = useState("none");

  // ---------------------------
  // Build Shopify snippet
  // ---------------------------
  const pixelId = useMemo(() => extractPixelId(pixelCode), [pixelCode]);
  const hostFromCode = useMemo(() => extractHost(pixelCode), [pixelCode]);

  const shopifySnippet = useMemo(
    () => buildShopifySnippet({ pixelId, host: hostFromCode || DEFAULT_HOST }),
    [pixelId, hostFromCode]
  );

  // clipboard hooks
  const {
    hasCopied: hasCopiedBase,
    onCopy: onCopyBase,
  } = useClipboard(pixelCode ?? '', { timeout: 2000 });

  const {
    hasCopied: hasCopiedShopify,
    onCopy: onCopyShopify,
  } = useClipboard(shopifySnippet ?? '', { timeout: 2000 });


  const { data: pipeLineList, isLoading: isPipelineLoading, isFetching: isPipelineFetching, refetch: refetchPipelineList } = useGetPipelinesQuery();
  const [createPipeline, { isLoading: isCreatingPipline }] = useCreatePipelineMutation();
  const [getPixelCode, { isLoading: isGettingPixelCode }] = useGetPixelCodeMutation();

  const handlePixelCode = (dataSourceId) => {
    getPixelCode({ payload: { dataSourceId } })
      .then((response) => {
        if (response?.error) {
          throw response.error;
        }
        const code = response?.data?.code || "";
        setPixelCode(code);
        setIntegrationType("none");
      })
      .catch((error) => {
        toast({
          position: "top-right",
          title: "Error getting pixel code",
          status: "error",
          description: error?.data?.message || error?.message || "Unknown error",
          duration: 4000,
          isClosable: true,
        });
      });
  };

  const columns = useMemo(
    () => [
      {
        Header: "Name",
        accessor: "",
        Filter: "",
        filter: "",
        Cell: ({ row: { original } }) => {
          return <Box>{original?.name}</Box>;
        }
      },
      {
        Header: "Data Sources",
        id: "dataSources",
        Cell: ({ row: { original } }) => {
          const sources = original?.dataSources;
          if (!Array.isArray(sources) || sources.length === 0) return <Box>-</Box>;

          return (
            <Box>
              {sources.map((source, idx) => (
                <Flex key={idx} align="center" gap={2}>
                  <Text>{source?.configuration?.name || "Unnamed Source"}</Text>
                  <Link
                    onClick={() => {
                      handlePixelCode(source?.sourceId);
                      onGetCodeModalOpen();
                      setSourceName(source?.configuration?.name);
                    }}
                    fontSize="sm"
                    color="brand.500"
                    _hover={{ textDecoration: 'underline' }}
                    cursor="pointer"
                  >
                    (Get Code)
                  </Link>
                </Flex>
              ))}
            </Box>
          );
        },
      },
      {
        Header: "Data Destinations",
        id: "dataDestinations",
        Cell: ({ row: { original } }) => {
          const destinations = original?.dataDestinations;
          if (!Array.isArray(destinations) || destinations.length === 0) {
            return <Text>—</Text>;
          }
          return (
            <Box>
              {destinations.map((dest, idx) => (
                <Text key={idx} fontSize={'12px'}>
                  {dest?.configuration?.name || "Unnamed Destination"}
                </Text>
              ))}
            </Box>
          );
        },
      },
    ],
    []
  );

  const formik = useFormik({
    initialValues: {
      metaAccessToken: '',
      metaDatasetId: '',
      businessId: '',
    },
    validationSchema: Yup.object({
      metaAccessToken: Yup.string().required('Required'),
      metaDatasetId: Yup.string().required('Required'),
      businessId: Yup.string().required('Required'),
    }),
    onSubmit: (values, { setSubmitting, resetForm }) => {
      createPipeline({ payload: values })
        .then(() => {
          refetchPipelineList();
          onPixelAddModalClose();
          toast({
            position: "top-right",
            title: "Pipeline created successfully",
            status: "success",
            duration: 4000,
            isClosable: true,
          });
        })
        .catch((error) => {
          toast({
            position: "top-right",
            title: "Error creating pipeline",
            status: "error",
            description: error?.data?.message,
            duration: 4000,
            isClosable: true,
          });
        })
        .finally(() => {
          setSubmitting(false);
          resetForm();
        });
    },
  });

  const handleClose = () => {
    formik.resetForm();
    onPixelAddModalClose();
  };

  return (
    <SidebarWithHeader>
      <Flex direction={"column"} gap={4} >
        <Flex alignItems="center" gap={4} justify={'space-between'}>
          <Heading as="h1" size="xl" color={textColor}>
            Signals Gateway
          </Heading>
          <Button colorScheme="brand" onClick={onPixelAddModalOpen} size={'sm'}>
            <AddIcon boxSize={3} />
            &nbsp;Create data pipeline
          </Button>
        </Flex>
        <Flex alignItems={"center"} mb={2}>
          <Text fontSize={"14px"} color={textColorSub}>
            With Signals Gateway, send your website, app, and offline conversion data directly to Meta, reducing signal loss and simplifying integration with Meta's marketing ecosystem. It improves data accuracy, protects user privacy, and helps your ads perform better by giving Meta more reliable information.
          </Text>
        </Flex>
      </Flex>

      {isPipelineLoading || isPipelineFetching ? (
        <Spinner mt="20px" />
      ) : (
        <Box mt={'20px'}>
          <AppTableWithPagination
            columns={columns}
            data={pipeLineList || []}
            paginationMeta={{}}
            onPageChange={() => { }}
            onSearchInputChange={(searchInput) => setsearchValue(searchInput)}
            searchEnabled={true}
            isFetching={''}
            emptyMessage={'Ready ? Click ‘Create’ to begin creating pipelines'}
          />
        </Box>
      )}

      {/* Create pipeline Modal */}
      <Modal isOpen={isPixelAddModalOpen} onClose={handleClose} size={'lg'}>
        <ModalOverlay />
        <ModalContent>
          <form onSubmit={formik.handleSubmit}>
            <ModalHeader>Create data pipeline</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Flex direction={'column'} gap={4}>

                {/* Business ID Field */}
                <Flex direction={'column'} gap={1}>
                  <Text fontWeight="bold" color={textColor}>
                    Business ID <Text as="span" color="red.400">*</Text>
                  </Text>
                  <Input
                    name="businessId"
                    placeholder="Enter Business ID"
                    bg="#fff"
                    value={formik.values.businessId}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={formik.touched.businessId && !!formik.errors.businessId}
                    size="sm"
                    borderRadius="md"
                  />
                  {formik.touched.businessId && formik.errors.businessId && (
                    <Text fontSize="sm" color="red.500">{formik.errors.businessId}</Text>
                  )}
                </Flex>

                {/* Dataset ID Field */}
                <Flex direction={'column'} gap={1}>
                  <Text fontWeight="bold" color={textColor}>
                    Dataset ID <Text as="span" color="red.400">*</Text>
                  </Text>
                  <Input
                    name="metaDatasetId"
                    placeholder="Enter Dataset ID"
                    bg="#fff"
                    value={formik.values.metaDatasetId}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={formik.touched.metaDatasetId && !!formik.errors.metaDatasetId}
                    size="sm"
                    borderRadius="md"
                  />
                  {formik.touched.metaDatasetId && formik.errors.metaDatasetId && (
                    <Text fontSize="sm" color="red.500">{formik.errors.metaDatasetId}</Text>
                  )}
                </Flex>

                {/* Access Token Field */}
                <Flex direction={'column'} gap={1}>
                  <Text fontWeight="bold" color={textColor}>
                    Access Token <Text as="span" color="red.400">*</Text>
                  </Text>
                  <Text fontSize="12px" color={textColor}>
                    To generate the Access Token, go to{' '}
                    <Link
                      href="https://business.facebook.com/"
                      isExternal
                      color="blue.500"
                      textDecoration="underline"
                    >
                      https://business.facebook.com/
                    </Link>{' '}
                    → <b>Business Settings</b> → <b>All Tools</b> → <b>Events Manager</b> → <b>Data Sources</b> → <b>Settings</b> → <b>Set up Direct Integration</b>, then click <b>Generate Access Token</b>.
                  </Text>
                  <Textarea
                    name="metaAccessToken"
                    placeholder="Enter the Access Token"
                    bg="#fff"
                    value={formik.values.metaAccessToken}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={formik.touched.metaAccessToken && !!formik.errors.metaAccessToken}
                    borderRadius="md"
                    size="sm"
                  />
                  {formik.touched.metaAccessToken && formik.errors.metaAccessToken && (
                    <Text fontSize="sm" color="red.500">{formik.errors.metaAccessToken}</Text>
                  )}
                </Flex>

              </Flex>
            </ModalBody>

            <ModalFooter gap={2}>
              <Button
                colorScheme="gray"
                variant="solid"
                size="sm"
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button
                colorScheme="brand"
                isLoading={formik.isSubmitting || isCreatingPipline}
                type="submit"
                size="sm"
                isDisabled={!formik.isValid}
              >
                Create
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>

      {/* Get Code Modal */}
      <Modal
        isOpen={isGetCodeModalOpen}
        onClose={() => {
          onGetCodeModalClose();
          setSourceName(null);
        }}
        isCentered
        size={"5xl"}
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{sourceName}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {isGettingPixelCode ?
              <Flex alignItems={'center'} justifyContent={'center'} height={'60vh'}>
                <Spinner />
              </Flex>
              :
              <Flex direction="column" gap={4}>
                <Flex alignItems={'center'} gap={2}>
                  <Text>Connect pixel data using a partner</Text>
                  <Tooltip
                    borderRadius={5}
                    label={
                      <Box fontSize="sm" p="5px">
                        <Text>Connect your website data using a partner, like Shopify.</Text>
                      </Box>
                    }
                    fontSize="md"
                  >
                    <InfoOutlineIcon pointerEvents="all" />
                  </Tooltip>
                </Flex>
                <Select
                  value={integrationType}
                  onChange={(e) => setIntegrationType(e.target.value)}
                  size="sm"
                  borderRadius={'md'}
                >
                  <option value="none">None</option>
                  <option value="shopify">Shopify</option>
                </Select>

                {/* Raw code (as returned) */}
                {integrationType === "none" && (
                  <>
                    <Flex direction={'column'} gap={2}>
                      <Text fontWeight={'bold'}>Add Signals Gateway Pixel to a website yourself</Text>
                      <Text fontSize={'12px'}>
                        {`Paste the pixel code into the bottom of the header section just above the </head> tag. Install the code on every page of your website.`}
                      </Text>
                    </Flex>

                    <Box>
                      <Box
                        bg={inputBackground}
                        p={3}
                        borderRadius="md"
                        fontFamily="mono"
                        fontSize="sm"
                        whiteSpace="pre"
                        maxH="300px"
                        overflow="auto"
                      >
                        <pre style={{ margin: 0, whiteSpace: 'pre' }}>
                          {pixelCode || '/* No code available */'}
                        </pre>
                      </Box>
                      {/* Copy button below-left */}
                      <Flex align="center" gap={3} mt={4}>
                        <Button
                          leftIcon={<CopyIcon />}
                          size="sm"
                          colorScheme="gray"
                          variant="outline"
                          onClick={onCopyBase}
                        >
                          Copy base code
                        </Button>
                        {hasCopiedBase && (
                          <Flex align="center" fontSize="sm" gap={1}>
                            <span>Copied</span>
                            <CheckCircleIcon color="green.500" />
                          </Flex>
                        )}
                      </Flex>
                    </Box>
                  </>
                )}


                {/* Shopify code (ready-to-copy) */}
                {integrationType === "shopify" && (
                  <Flex direction="column" gap={3}>
                    <Text fontSize="sm">
                      If you're using Shopify for your website, the Signals Gateway Pixel can automatically record events such as purchase, add to cart, and more. You can deploy this through the "Customer Events" feature on the admin page of your Shopify website. Follow the{" "}
                      <Link
                        href="https://help.shopify.com/en/manual/promoting-marketing/pixels"
                        isExternal
                        color={'#8671cc'}
                        _hover={{ textDecoration: "underline" }}
                        textDecoration="none"
                      >
                        Shopify pixels and customer events guidance
                      </Link>{" "}
                      for step-by-step instructions.
                    </Text>


                    {/* Show warning if pixelId couldn't be parsed */}
                    {!pixelId && (
                      <Text color="red.500" fontSize="sm">
                        Could not detect a Pixel ID in the returned code. Make sure your code contains a line like:
                        <br />
                        <code>cbq('init', '123456789012345');</code>
                      </Text>
                    )}

                    <Box
                      bg={inputBackground}
                      p={3}
                      borderRadius="md"
                      fontFamily="mono"
                      fontSize="sm"
                      whiteSpace="pre"
                      maxH="300px"
                      overflow="auto"
                    >
                      <pre style={{ margin: 0, whiteSpace: 'pre' }}>
                        {shopifySnippet || '/* No Shopify snippet available */'}
                      </pre>
                    </Box>
                    {/* Copy button below-left */}
                    <Flex align="center" gap={3} mt={2}>
                      <Button
                        leftIcon={<CopyIcon />}
                        size="sm"
                        colorScheme="gray"
                        variant="outline"
                        onClick={onCopyShopify}
                        isDisabled={!shopifySnippet}
                      >
                        Copy custom pixel code
                      </Button>
                      {hasCopiedShopify && (
                        <Flex align="center" fontSize="sm" gap={1}>
                          <span>Copied</span>
                          <CheckCircleIcon color="green.500" />
                        </Flex>
                      )}
                    </Flex>

                  </Flex>
                )}
              </Flex>
            }
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="gray"
              variant="solid"
              size={"sm"}
              onClick={() => {
                onGetCodeModalClose();
                setSourceName(null);
              }}
            >
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </SidebarWithHeader>
  );
}

export default SignalGateway;
